import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "../lib/cart";
import { api, formatPrice, productImage } from "../lib/api";
import { useLanguage } from "../lib/language";
import { Trash2 } from "lucide-react";

export default function Cart() {
    const { items, remove, setQty, total, originalTotal, siteDiscountAmount, effectivePrice } = useCart();
    const nav = useNavigate();
    const [settings, setSettings] = useState({});
    const { t } = useLanguage();

    useEffect(() => { api.get("/settings").then(({ data }) => setSettings(data)); }, []);

    if (items.length === 0) {
        return (
            <div className="max-w-3xl mx-auto px-6 py-24 text-center fade-in">
                <h1 className="font-display text-4xl mb-4">{t("Tu carrito está vacío", "Your cart is empty")}</h1>
                <p className="text-[var(--tb-text-2)] mb-8">{t("Explora nuestra colección curada y agrega tus piezas favoritas.", "Browse our curated collection and add your favorite pieces.")}</p>
                <Link to="/tienda" className="tb-btn-primary inline-block" data-testid="empty-cart-shop">{t("Ir a la tienda", "Go to Shop")}</Link>
            </div>
        );
    }

    const shipPrice = Number(settings.shipping_price || 0);
    const freeTh = Number(settings.free_shipping_threshold || 0);
    const shipping = (freeTh > 0 && total >= freeTh) ? 0 : shipPrice;
    const grand = total + shipping;

    return (
        <div className="max-w-5xl mx-auto px-6 py-12 fade-in">
            <h1 className="font-display text-4xl mb-8">{t("Carrito", "Cart")}</h1>
            <div className="grid md:grid-cols-3 gap-8">
                <div className="md:col-span-2 space-y-4">
                    {items.map((i) => {
                        const finalPrice = effectivePrice(i);
                        const hasDiscount = finalPrice < i.precio;
                        return (
                            <div key={i.sku} className="tb-card p-4 flex gap-4 items-center" data-testid={`cart-item-${i.sku}`}>
                                <img src={productImage(i)} alt={i.nombre} className="w-24 h-24 object-cover" />
                                <div className="flex-1">
                                    <div className="font-display text-lg">{i.nombre}</div>
                                    <div className="text-xs text-[var(--tb-muted)]">{i.sku}</div>
                                    <div className="flex items-baseline gap-2 mt-1">
                                        <div className="text-[var(--tb-gold)]">{formatPrice(finalPrice)}</div>
                                        {hasDiscount && <div className="text-xs text-[var(--tb-muted)] line-through">{formatPrice(i.precio)}</div>}
                                    </div>
                                </div>
                                <input type="number" min="1" className="tb-input w-20"
                                    value={i.quantity} onChange={(e) => setQty(i.sku, parseInt(e.target.value || 1))} />
                                <button className="tb-link" onClick={() => remove(i.sku)} data-testid={`cart-remove-${i.sku}`}>
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        );
                    })}
                </div>
                <div className="tb-card p-6 h-fit">
                    <div className="tb-overline text-[var(--tb-gold)] mb-4">{t("Resumen", "Summary")}</div>
                    {siteDiscountAmount > 0 && (
                        <div className="flex justify-between mb-2 text-sm text-[var(--tb-muted)]">
                            <span>{t("Subtotal original", "Original subtotal")}</span>
                            <span className="line-through">{formatPrice(originalTotal)}</span>
                        </div>
                    )}
                    {siteDiscountAmount > 0 && (
                        <div className="flex justify-between mb-2 text-sm text-[#4CAF50]" data-testid="cart-site-discount">
                            <span>{t("Promoción aplicada", "Promo applied")}</span>
                            <span>-{formatPrice(siteDiscountAmount)}</span>
                        </div>
                    )}
                    <div className="flex justify-between mb-2"><span>{t("Subtotal", "Subtotal")}</span><span>{formatPrice(total)}</span></div>
                    <div className="flex justify-between mb-2 text-sm">
                        <span>{t("Envío", "Shipping")}</span>
                        <span className={shipping === 0 ? "text-[#4CAF50]" : ""}>
                            {shipping === 0 ? (freeTh > 0 ? t("Gratis", "Free") : "—") : formatPrice(shipping)}
                        </span>
                    </div>
                    {freeTh > 0 && total < freeTh && (
                        <div className="text-xs text-[var(--tb-text-2)] mb-4">
                            {t(`Te faltan ${formatPrice(freeTh - total)} para envío gratis.`, `${formatPrice(freeTh - total)} away from free shipping.`)}
                        </div>
                    )}
                    <div className="border-t border-[var(--tb-border)] pt-4 flex justify-between text-xl mb-6">
                        <span>{t("Total", "Total")}</span><span className="text-[var(--tb-gold)] font-semibold">{formatPrice(grand)} USD</span>
                    </div>
                    <button onClick={() => nav("/checkout")} className="tb-btn-primary w-full" data-testid="checkout-button">
                        {t("Proceder al pago", "Proceed to checkout")}
                    </button>
                </div>
            </div>
        </div>
    );
}
